<template>
    <div class="reports-payment">
        <div class="reports-payment__top">
            <div class="reports-payment__select">
                <v-text-field
                    v-model="requestData.from"
                    dense
                    outlined
                    type="date"
                    name="from"
                    :label="textContent['from']"
                    hide-details
                ></v-text-field>
            </div>
            <div class="reports-payment__select">
                <v-text-field
                    v-model="requestData.to"
                    dense
                    outlined
                    type="date"
                    name="to"
                    :label="textContent['to']"
                    hide-details
                ></v-text-field>
            </div>
            <v-btn
                class="reports-payment__btn"
                dark
                color="#967adc"
                :loading="isTableLoading"
                @click="datePaymentsHandle"
            >
                {{ $t('pages.report.reload') }}
            </v-btn>
            <v-btn
                class="reports-payment__btn-pdf"
                :loading="isPdfLoading"
                outlined
                dark
                color="#967adc"
                @click="generatePdf"
                >pdf
            </v-btn>
        </div>

        <template>
            <ReportsPaymentTable
                @filters-change="updateFilters"
                @sort-change="updateSort"
            />
        </template>

        <v-pagination
            v-if="pagination && pagination.lastPage > 1"
            v-model="page"
            class="pagination"
            :total-visible="7"
            :length="pagination.lastPage"
            color="#3bafda"
            @input="onPageChange"
        ></v-pagination>
    </div>
</template>

<script>
import ReportsPaymentTable from '@/components/reports/reports-payment-table'
import languageMixin from '@/mixins/language.mixin'
import languageOrderExtraMixin from '@/mixins/language-order-extra.mixin'
import { mapActions, mapGetters } from 'vuex'
import { actionTypes, getterTypes } from '@/store/modules/report'
import regularToSnakeCase from '@/helpers/regular-to-snake-case'
import { buildDateParams } from '@/helpers/build-date-params.utils'

export default {
    name: 'ReportsPayment',
    components: { ReportsPaymentTable },
    mixins: [languageMixin, languageOrderExtraMixin],
    data: () => ({
        employees: false,
        requestData: {
            from: '',
            to: '',
        },

        localFilters: {
            client: [],
            employee: [],
            source: [],
        },

        appliedFilters: {
            client: [],
            employee: [],
            source: [],
        },

        page: 1,
        perPage: 20,

        sort: [],

        isTableLoading: false,
        isPdfLoading: false,
    }),
    computed: {
        ...mapGetters('report', {
            getAllPayments: getterTypes.getAllPayments,
        }),
        pagination: function () {
            return this.getAllPayments.pagination ?? null
        },
    },
    mounted() {
        this.presentDate()
        this.datePaymentsHandle()
        if (this.employees) {
            this.dateEmployeesHandle()
        }
    },
    methods: {
        ...mapActions('report', {
            getPayments: actionTypes.getPayments,
            getPaymentsPdf: actionTypes.getPaymentsByPdf,
            getEmployees: actionTypes.getEmployees,
            getEmployeesPdf: actionTypes.getEmployeesByPdf,
        }),
        addZero: function (data) {
            return data < 10 ? '0' + data : data
        },
        presentDate: function () {
            const date = new Date()
            const year = date.getFullYear()

            const month = this.addZero(date.getMonth() + 1)
            const day = this.addZero(date.getDate())

            this.requestData.from = `${year}-${month}-${day}`
            this.requestData.to = `${year}-${month}-${day}`
        },
        getStatus: function (status) {
            return this.extraContent[regularToSnakeCase(status)]
        },
        fetchPayments: async function () {
            try {
                this.isTableLoading = true
                const dates = buildDateParams(
                    this.requestData.from,
                    this.requestData.to
                )

                await this.getPayments({
                    ...dates,
                    client: this.appliedFilters.client,
                    employee: this.appliedFilters.employee,
                    source: this.appliedFilters.source,
                    page: this.page,
                    per_page: this.perPage,
                    sort: this.sort,
                })
            } finally {
                this.isTableLoading = false
            }
        },
        datePaymentsHandle: function () {
            if (this.isTableLoading) return

            this.appliedFilters = { ...this.localFilters }
            this.page = 1
            this.fetchPayments()
        },
        generatePdf: async function () {
            if (this.isPdfLoading) return

            this.isPdfLoading = true

            const payload = {
                from: `${this.requestData.from} 00:00:00`,
                to: `${this.requestData.to} 23:59:59`,
                client: this.appliedFilters.client,
                employee: this.appliedFilters.employee,
                source: this.appliedFilters.source,
                sort: this.sort,
                format: 'pdf',
            }

            const handle = this.employees
                ? this.getEmployeesPdf
                : this.getPaymentsPdf

            try {
                const response = await handle(payload)

                const fileUrl = response?.url
                if (!fileUrl) {
                    console.error('No PDF URL in response')
                    return
                }

                window.open(fileUrl, '_blank')
            } catch (e) {
                console.error('PDF download error:', e)
            } finally {
                this.isPdfLoading = false
            }
        },
        updateFilters: function (filters) {
            this.localFilters = filters
        },
        onPageChange: function (page) {
            this.page = page
            this.fetchPayments()
        },
        updateSort: function (value) {
            this.sort = value
            this.page = 1
            this.fetchPayments()
        },
    },
}
</script>

<style lang="scss" scoped>
.reports-payment {
    &__top {
        display: flex;
        flex-wrap: wrap;
        margin-bottom: 16px;
    }

    &__switch {
        display: flex;
        align-items: center;
    }

    &__text {
        margin-right: 7px;
        font-size: 16px;

        @include for-hebrew {
            margin-right: 0;
            margin-left: 7px;
        }
    }

    &__select {
        height: 57px;
        max-width: 200px;
        width: 100%;

        & + & {
            margin: 0 10px;
        }
    }

    &__name {
        color: #939393;
    }

    &__btn {
        max-width: 130px;
        height: 40px;
    }

    &__btn-pdf {
        margin-left: 15px;

        @include for-hebrew {
            margin-left: 0;
            margin-right: 15px;
        }
    }

    &__loading {
        margin-left: 15px;
        margin-top: 6px;

        @include for-hebrew {
            margin-left: 0;
            margin-right: 15px;
        }
    }

    td {
        text-transform: capitalize;
    }
}

.v-btn--disabled {
    opacity: 0.6 !important;
    pointer-events: none !important;
    visibility: visible !important;
}
</style>
